<template>
  <div class="event-explore-filter">
    <div class="form-group">
      <label class="form-label">{{ copy.filter.country }}</label>
      <select
        class="form-control form-control-sm"
        v-model="selectedCountry"
        @change="handleCountryChange"
      >
        <option
          v-for="option in formData.countryOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="form-group">
      <label class="form-label">{{ copy.filter.region }}</label>
      <select
        class="form-control form-control-sm"
        v-model="selectedRegion"
        @change="emitFilterChange"
      >
        <option value="">{{ copy.filter.allRegions }}</option>
        <option
          v-for="option in regionOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="form-group">
      <label class="form-label">
        {{ copy.filter.distance }}
        ({{ distanceRange[0] }} - {{ distanceRange[1] }}{{ maxDistanceIndicator }} {{ distanceUnits }})
      </label>
      <hd-range-slider
        ref="distanceSlider"
        v-model="distanceRange"
        :max="100"
        :min="1"
        :min-range="1"
        :interval="1"
      ></hd-range-slider>
    </div>

    <div class="form-group">
      <label class="form-label">{{ copy.filter.activityType }}</label>
      <select
        class="form-control form-control-sm"
        v-model="selectedActivityType"
        @change="handleActivityTypeChange"
      >
        <option
          v-for="option in formData.activityTypes"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="form-group">
      <label class="form-label">{{ copy.filter.terrain }}</label>
      <select
        class="form-control form-control-sm"
        v-model="selectedTerrain"
        @change="emitFilterChange"
        :disabled="terrainDisabled"
        :aria-describedby="terrainDisabled ? 'event-explore-terrain-note' : null"
      >
        <option value="">{{ copy.filter.allTerrain }}</option>
        <option
          v-for="option in formData.terrainTypes"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <small
        v-if="terrainDisabled"
        id="event-explore-terrain-note"
        class="form-text text-muted"
      >
        {{ copy.filter.terrainSwimNote }}
      </small>
    </div>

    <h6 class="mt-4 mb-3">{{ copy.filter.dateRange }}</h6>

    <div class="row">
      <div class="col-6">
        <div class="form-group">
          <label class="form-label small">{{ copy.filter.after }}</label>
          <input
            type="date"
            class="form-control form-control-sm"
            v-model="startDate"
            @change="emitFilterChange"
          >
        </div>
      </div>
      <div class="col-6">
        <div class="form-group">
          <label class="form-label small">{{ copy.filter.before }}</label>
          <input
            type="date"
            class="form-control form-control-sm"
            v-model="endDate"
            @change="emitFilterChange"
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { HdRangeSlider } from '../../ui';
import { terrainDisabledFor, terrainFor } from '../../utilities/event_filters';

export default {
  props: ['formData', 'useMetric', 'copy', 'currentFilters'],

  components: { HdRangeSlider },

  data() {
    const filters = this.currentFilters || {};
    const activityType = filters.activity_type ||
      this.formData.activityTypes[0]?.value || null;
    return {
      selectedCountry: filters.country || this.formData.countryOptions[0]?.value,
      selectedRegion: filters.region || '',
      selectedActivityType: activityType,
      selectedTerrain: terrainFor(activityType, filters.terrain) || '',
      distanceRange: this.initializeDistanceRange(filters.distance),
      startDate: filters.date_range?.start || this.formData.startDate,
      endDate: filters.date_range?.end || this.formData.endDate,
      regionOptions: this.formData.regionOptions || []
    };
  },

  watch: {
    distanceRange: {
      handler() {
        this.emitFilterChange();
      },
      deep: true
    }
  },

  computed: {
    distanceUnits() {
      return this.useMetric ? 'km' : 'mi';
    },

    maxDistanceIndicator() {
      return this.distanceRange[1] >= 100 ? '+' : '';
    },

    terrainDisabled() {
      return terrainDisabledFor(this.selectedActivityType);
    }
  },

  methods: {
    handleActivityTypeChange() {
      // Drop any terrain the searcher had picked before switching to swim, so
      // the locked-out select can't keep narrowing the results invisibly.
      if (this.terrainDisabled) {
        this.selectedTerrain = '';
      }
      this.emitFilterChange();
    },

    initializeDistanceRange(distance) {
      if (!distance) {
        return [1, 100];
      }

      const conversionRate = this.useMetric ? 1000 : 1609;
      return [
        Math.round(distance.min / conversionRate),
        Math.round(distance.max / conversionRate)
      ];
    },

    handleCountryChange() {
      this.selectedRegion = '';
      this.fetchRegions();
      this.emitFilterChange();
    },

    fetchRegions() {
      const self = this;
      axios.get(`/geography/countries/${this.selectedCountry}/regions`, {
        headers: { 'Accept': 'application/json' }
      }).then(response => {
        self.regionOptions = response.data.regions.map(region => ({
          value: region.value,
          label: region.name
        }));
      }).catch(error => {
        console.error('Error fetching regions:', error);
        self.regionOptions = [];
      });
    },

    emitFilterChange() {
      const conversionRate = this.useMetric ? 1000 : 1609;

      this.$emit('filter-change', {
        country: this.selectedCountry,
        region: this.selectedRegion || null,
        activity_type: this.selectedActivityType,
        terrain: terrainFor(this.selectedActivityType, this.selectedTerrain),
        distance: {
          min: this.distanceRange[0] * conversionRate,
          max: this.distanceRange[1] * conversionRate,
          min_reached: this.distanceRange[0] <= 1,
          max_reached: this.distanceRange[1] >= 100
        },
        date_range: {
          start: this.startDate,
          end: this.endDate
        }
      });
    }
  }
};
</script>

<style scoped>
.event-explore-filter .form-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.event-explore-filter .form-group {
  margin-bottom: 1rem;
}

.event-explore-filter h6 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}
</style>
